import { useState, useRef, useEffect, useReducer, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  Instagram,
  Facebook,
  Globe,
  Phone,
  Video,
  MoreVertical,
  ArrowLeftRight,
  XCircle,
  Check,
  CheckCheck,
  FileText,
  MapPin,
  Mic,
  Clock,
  Download,
  Play,
  Bot,
  UserCheck,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { MessageInput } from "./MessageInput";
import { TransferTicketModal } from "./TransferTicketModal";
import type { Conversation } from "./InboxList";
import { format, parseISO, isSameDay } from "date-fns";

// Import from existing system
import api from "../../../services/api";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { i18n } from "../../../translate/i18n";

export type Message = {
  id: string;
  content: string;
  type: "text" | "image" | "audio" | "file" | "location" | "video" | "sticker";
  sender: "client" | "agent" | "system";
  timestamp: string;
  createdAt: string;
  status: "sending" | "sent" | "delivered" | "read";
  fileName?: string;
  fileSize?: string;
  mediaUrl?: string;
  imageUrl?: string;
  audioUrl?: string;
  duration?: string;
  location?: { address: string; lat: number; lng: number };
  quotedMsg?: any;
  isDeleted?: boolean;
};

const channelIcons: Record<string, { icon: any; color: string; label: string }> = {
  whatsapp: { icon: MessageCircle, color: "#22C55E", label: "WhatsApp" },
  instagram: { icon: Instagram, color: "#E4405F", label: "Instagram" },
  facebook: { icon: Facebook, color: "#1877F2", label: "Facebook" },
  widget: { icon: Globe, color: "#2563EB", label: "Chat Widget" },
  webchat: { icon: Globe, color: "#2563EB", label: "Chat Widget" },
};

// Reducer for messages
const messagesReducer = (state: Message[], action: any): Message[] => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages: Message[] = [];
    messages.forEach((message: Message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });
    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);
    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }
    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);
    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    } else {
      state.push(messageToUpdate);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

// Map API message to our Message type
const mapApiMessage = (msg: any): Message => {
  let type: Message["type"] = "text";
  if (msg.mediaType === "image") type = "image";
  else if (msg.mediaType === "audio") type = "audio";
  else if (msg.mediaType === "video") type = "video";
  else if (msg.mediaType === "application") type = "file";
  else if (msg.mediaType === "locationMessage") type = "location";
  else if (msg.mediaType === "sticker") type = "sticker";

  let status: Message["status"] = "sent";
  if (msg.ack === 0) status = "sending";
  else if (msg.ack === 1) status = "sent";
  else if (msg.ack === 2) status = "delivered";
  else if (msg.ack >= 3) status = "read";

  let timestamp = "";
  let createdAt = msg.createdAt || "";
  try {
    timestamp = format(parseISO(msg.createdAt), "HH:mm");
  } catch {
    timestamp = "";
  }

  return {
    id: String(msg.id),
    content: msg.body || "",
    type,
    sender: msg.fromMe ? "agent" : "client",
    timestamp,
    createdAt,
    status,
    mediaUrl: msg.mediaUrl,
    imageUrl: msg.mediaType === "image" ? msg.mediaUrl : undefined,
    audioUrl: msg.mediaType === "audio" ? msg.mediaUrl : undefined,
    fileName: msg.mediaUrl ? msg.mediaUrl.split("/").pop() : undefined,
    quotedMsg: msg.quotedMsg,
    isDeleted: msg.isDeleted,
  };
};

interface ChatWindowProps {
  conversation: Conversation | null;
  onTicketClosed?: () => void;
}

export function ChatWindow({ conversation, onTicketClosed }: ChatWindowProps) {
  const [messages, dispatch] = useReducer(messagesReducer, []);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentTicketId = useRef<string | null>(null);
  const [isBot, setIsBot] = useState(false);
  const [togglingBot, setTogglingBot] = useState(false);
  const [acceptingTicket, setAcceptingTicket] = useState(false);
  const [ticketStatus, setTicketStatus] = useState<string>("pending");
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [closingTicket, setClosingTicket] = useState(false);
  const [reopeningTicket, setReopeningTicket] = useState(false);

  const { user, socket } = useContext(AuthContext) as any;
  const companyId = user?.companyId;

  // Atualiza estado do bot e status quando muda a conversa
  useEffect(() => {
    if (conversation?.originalTicket) {
      setIsBot(conversation.originalTicket.isBot !== false);
      setTicketStatus(conversation.originalTicket.status || conversation.status || "pending");
    } else if (conversation) {
      setTicketStatus(conversation.status || "pending");
    }
  }, [conversation?.id]);

  // Aceitar ticket
  const handleAcceptTicket = async () => {
    if (!conversation) return;

    setAcceptingTicket(true);
    try {
      const isGroup = conversation.originalTicket?.isGroup || false;
      const newStatus = isGroup ? "group" : "open";

      await api.put(`/tickets/${conversation.id}`, {
        status: newStatus,
        userId: user?.id,
      });

      setTicketStatus(newStatus);
    } catch (err) {
      console.error("Erro ao aceitar ticket:", err);
    } finally {
      setAcceptingTicket(false);
    }
  };

  // Toggle IA do ticket
  const handleToggleBot = async () => {
    if (!conversation) return;

    setTogglingBot(true);
    try {
      const newValue = !isBot;
      setIsBot(newValue);

      const { data } = await api.put(`/tickets/${conversation.id}/toggle-bot`);

      if (data?.isBot !== undefined) {
        setIsBot(data.isBot);
      }
    } catch (err) {
      setIsBot(!isBot); // Reverte em caso de erro
      console.error("Erro ao alternar IA:", err);
    } finally {
      setTogglingBot(false);
    }
  };

  // Fechar ticket
  const handleCloseTicket = async () => {
    if (!conversation) return;

    setClosingTicket(true);
    try {
      await api.put(`/tickets/${conversation.id}`, {
        status: "closed",
        userId: user?.id,
      });

      setTicketStatus("closed");

      // Limpa a conversa selecionada após fechar
      if (onTicketClosed) {
        onTicketClosed();
      }
    } catch (err) {
      console.error("Erro ao fechar ticket:", err);
    } finally {
      setClosingTicket(false);
    }
  };

  // Reabrir ticket
  const handleReopenTicket = async () => {
    if (!conversation) return;

    setReopeningTicket(true);
    try {
      const isGroup = conversation.originalTicket?.isGroup || false;
      const newStatus = isGroup ? "group" : "open";

      await api.put(`/tickets/${conversation.id}`, {
        status: newStatus,
        userId: user?.id,
      });

      setTicketStatus(newStatus);
    } catch (err) {
      console.error("Erro ao reabrir ticket:", err);
    } finally {
      setReopeningTicket(false);
    }
  };

  // Reset and fetch messages when conversation changes
  useEffect(() => {
    if (!conversation) return;

    dispatch({ type: "RESET" });
    setPageNumber(1);
    currentTicketId.current = conversation.id;

    const fetchMessages = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/messages/${conversation.id}`, {
          params: { pageNumber: 1 },
        });

        if (currentTicketId.current === conversation.id) {
          const mappedMessages = data.messages.map(mapApiMessage);
          dispatch({ type: "LOAD_MESSAGES", payload: mappedMessages });
          setHasMore(data.hasMore);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching messages:", err);
        setLoading(false);
      }
    };

    fetchMessages();
  }, [conversation?.id]);

  // Load more messages when scrolling up
  useEffect(() => {
    if (pageNumber === 1 || !conversation) return;

    const fetchMoreMessages = async () => {
      try {
        const { data } = await api.get(`/messages/${conversation.id}`, {
          params: { pageNumber },
        });

        if (currentTicketId.current === conversation.id) {
          const mappedMessages = data.messages.map(mapApiMessage);
          dispatch({ type: "LOAD_MESSAGES", payload: mappedMessages });
          setHasMore(data.hasMore);
        }
      } catch (err) {
        console.error("Error fetching more messages:", err);
      }
    };

    fetchMoreMessages();
  }, [pageNumber, conversation?.id]);

  // Socket listeners for real-time updates
  useEffect(() => {
    if (!socket || !companyId || typeof socket.on !== "function") return;
    if (!conversation) return;

    const ticketUuid = conversation.uuid || conversation.id;

    // Join the chat room
    if (typeof socket.emit === "function") {
      socket.emit("joinChatBox", ticketUuid);
    }

    const onAppMessage = (data: any) => {
      const evtUuid = data?.message?.ticket?.uuid || data?.ticket?.uuid;
      const evtTicketId = data?.message?.ticketId || data?.ticketId;

      // Check if message belongs to current conversation
      const belongsToConversation =
        evtUuid === ticketUuid ||
        String(evtTicketId) === String(conversation.id);

      if (!belongsToConversation) return;

      if (data.action === "create" && data.message) {
        const mappedMessage = mapApiMessage(data.message);
        dispatch({ type: "ADD_MESSAGE", payload: mappedMessage });
        scrollToBottom();
      }

      if (data.action === "update" && data.message) {
        const mappedMessage = mapApiMessage(data.message);
        dispatch({ type: "UPDATE_MESSAGE", payload: mappedMessage });
      }
    };

    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      if (typeof socket.emit === "function") {
        socket.emit("joinChatBoxLeave", ticketUuid);
      }
      if (typeof socket.off === "function") {
        socket.off(`company-${companyId}-appMessage`, onAppMessage);
      }
    };
  }, [socket, companyId, conversation?.id, conversation?.uuid]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (pageNumber === 1 && messages.length > 0) {
      // Usa timeout maior para garantir que as mensagens foram renderizadas
      scrollToBottom(!loading);
    }
  }, [messages.length, pageNumber, loading]);

  const scrollToBottom = (smooth = true) => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: smooth ? "smooth" : "auto"
      });
    }, 150);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || loading) return;
    const { scrollTop } = e.currentTarget;
    if (scrollTop < 50) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!conversation) return;

    try {
      await api.post(`/messages/${conversation.id}`, {
        body: content,
        fromMe: true,
      });
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  if (!conversation) {
    return (
      <div className="flex flex-1 items-center justify-center bg-[var(--bg-primary)] transition-colors duration-300">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[var(--card-bg)] shadow-sm">
            <MessageCircle className="h-10 w-10 text-[var(--text-muted)]" />
          </div>
          <h3 className="mt-6 text-lg font-semibold text-[var(--text-primary)]">
            {i18n.t("v2.chat.selectConversation")}
          </h3>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            {i18n.t("v2.chat.selectConversationHint")}
          </p>
        </motion.div>
      </div>
    );
  }

  const channelData = channelIcons[conversation.channel] || channelIcons.whatsapp;
  const ChannelIcon = channelData.icon;

  // Group messages by date
  const getDateSeparator = (message: Message, index: number, allMessages: Message[]) => {
    if (index === 0) return true;
    try {
      const currentDate = parseISO(message.createdAt);
      const prevDate = parseISO(allMessages[index - 1].createdAt);
      return !isSameDay(currentDate, prevDate);
    } catch {
      return false;
    }
  };

  const formatDateSeparator = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      const today = new Date();
      if (isSameDay(date, today)) return i18n.t("v2.chat.today");
      return format(date, "dd/MM/yyyy");
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-[var(--bg-primary)] transition-colors duration-300">
      {/* Chat Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-shrink-0 flex items-center justify-between border-b border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-3 sm:px-6 sm:py-4 transition-colors duration-300"
      >
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
          <div className="relative flex-shrink-0">
            <img
              src={conversation.clientAvatar}
              alt={conversation.clientName}
              className="h-9 w-9 sm:h-11 sm:w-11 rounded-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.clientName)}&background=random`;
              }}
            />
            {conversation.isOnline && (
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full border-2 border-[var(--card-bg)] bg-[#22C55E]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm sm:text-base font-semibold text-[var(--text-primary)] truncate">
                {conversation.clientName}
              </h3>
              <div
                className="hidden sm:flex items-center gap-1 rounded-full px-2 py-0.5 flex-shrink-0"
                style={{ backgroundColor: `${channelData.color}15` }}
              >
                <ChannelIcon
                  className="h-3 w-3"
                  style={{ color: channelData.color }}
                />
                <span
                  className="text-xs font-medium"
                  style={{ color: channelData.color }}
                >
                  {channelData.label}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-[var(--text-secondary)] truncate">
                <span className="hidden sm:inline">{i18n.t("v2.chat.agent")}: </span>{conversation.agentName}
              </span>
              <span className="text-[var(--border-color)] hidden sm:inline">•</span>
              <span className="text-xs text-[var(--text-secondary)] hidden sm:inline">
                #{conversation.id}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Botão Aceitar - só aparece quando ticket está pending */}
          {ticketStatus === "pending" && (
            <button
              onClick={handleAcceptTicket}
              disabled={acceptingTicket}
              className="flex items-center gap-1.5 sm:gap-2 rounded-xl bg-[#2563EB] px-2.5 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-[#1E40AF] disabled:opacity-50"
            >
              <UserCheck className={cn("h-4 w-4", acceptingTicket && "animate-pulse")} />
              <span className="hidden sm:inline">
                {acceptingTicket ? i18n.t("v2.chat.actions.accepting") : i18n.t("v2.chat.actions.accept")}
              </span>
            </button>
          )}
          {/* Botões que aparecem quando ticket foi aceito */}
          {(ticketStatus === "open" || ticketStatus === "group") && (
            <>
              {/* Phone e Video - esconde em mobile muito pequeno */}
              <button className="hidden sm:flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]">
                <Phone className="h-4 w-4" />
              </button>
              <button className="hidden sm:flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]">
                <Video className="h-4 w-4" />
              </button>
              <div className="hidden sm:block h-6 w-px bg-[var(--border-color)]" />
              {/* Botão Transferir */}
              <button
                onClick={() => setTransferModalOpen(true)}
                className="flex h-8 w-8 sm:h-auto sm:w-auto items-center justify-center sm:gap-2 rounded-xl sm:px-3 sm:py-2 text-sm font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
              >
                <ArrowLeftRight className="h-4 w-4" />
                <span className="hidden md:inline">{i18n.t("v2.chat.actions.transfer")}</span>
              </button>
              {/* Botão IA */}
              <button
                onClick={handleToggleBot}
                disabled={togglingBot}
                className={cn(
                  "flex h-8 w-8 sm:h-auto sm:w-auto items-center justify-center sm:gap-2 rounded-xl sm:px-3 sm:py-2 text-sm font-medium transition-all",
                  isBot
                    ? "bg-[#22C55E]/10 text-[#22C55E] hover:bg-[#22C55E]/20"
                    : "bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
                )}
              >
                <Bot className={cn("h-4 w-4", togglingBot && "animate-pulse")} />
                <span className="hidden md:inline">
                  {isBot ? i18n.t("v2.chat.ai.active") : i18n.t("v2.chat.ai.inactive")}
                </span>
                <div
                  className={cn(
                    "hidden sm:block h-2 w-2 rounded-full",
                    isBot ? "bg-[#22C55E] animate-pulse" : "bg-[var(--text-muted)]"
                  )}
                />
              </button>
              {/* Botão Fechar */}
              <button
                onClick={handleCloseTicket}
                disabled={closingTicket}
                className="flex h-8 w-8 sm:h-auto sm:w-auto items-center justify-center sm:gap-2 rounded-xl sm:px-3 sm:py-2 text-sm font-medium text-[#EF4444] transition-colors hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-50"
              >
                <XCircle className={cn("h-4 w-4", closingTicket && "animate-pulse")} />
                <span className="hidden md:inline">{i18n.t("v2.chat.actions.close")}</span>
              </button>
            </>
          )}
          {/* Botão Reabrir - aparece quando ticket está fechado */}
          {ticketStatus === "closed" && (
            <button
              onClick={handleReopenTicket}
              disabled={reopeningTicket}
              className="flex items-center gap-1.5 sm:gap-2 rounded-xl bg-[#22C55E] px-2.5 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white shadow-lg shadow-green-500/25 transition-all hover:bg-[#16A34A] disabled:opacity-50"
            >
              <RotateCcw className={cn("h-4 w-4", reopeningTicket && "animate-spin")} />
              <span className="hidden sm:inline">{i18n.t("v2.chat.actions.reopen")}</span>
            </button>
          )}
          <button className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]">
            <MoreVertical className="h-4 w-4" />
          </button>
        </div>
      </motion.div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-6" onScroll={handleScroll}>
        <div className="mx-auto max-w-3xl space-y-3 sm:space-y-4">
          {loading && pageNumber === 1 ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#2563EB] border-t-transparent" />
            </div>
          ) : (
            <AnimatePresence>
              {messages.map((message, index) => (
                <div key={message.id}>
                  {/* Date Separator */}
                  {getDateSeparator(message, index, messages) && (
                    <div className="flex items-center justify-center py-4">
                      <span className="rounded-full bg-[var(--card-bg)] px-4 py-1.5 text-xs font-medium text-[var(--text-secondary)] shadow-sm">
                        {formatDateSeparator(message.createdAt)}
                      </span>
                    </div>
                  )}
                  <MessageBubble message={message} index={index} />
                </div>
              ))}
            </AnimatePresence>
          )}

          {messages.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <MessageCircle className="h-12 w-12 text-[var(--text-muted)]" />
              <p className="mt-4 text-sm text-[var(--text-secondary)]">
                {i18n.t("v2.chat.sayHello")}
              </p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <MessageInput onSendMessage={handleSendMessage} disabled={isBot} />

      {/* Transfer Ticket Modal */}
      <TransferTicketModal
        open={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        ticketId={conversation ? Number(conversation.id) : null}
        ticket={conversation?.originalTicket || conversation}
        onTransferSuccess={() => {
          setTransferModalOpen(false);
          // O socket vai atualizar a lista de tickets automaticamente
        }}
      />
    </div>
  );
}
// Coloque ANTES do MessageBubble

const extractUrls = (text: string): string[] => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.match(urlRegex) || [];
};

function LinkPreview({ url }: { url: string }) {
  const [preview, setPreview] = useState<{
    title?: string;
    description?: string;
    image?: string;
    siteName?: string;
    loading: boolean;
    error: boolean;
  }>({ loading: true, error: false });

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const response = await fetch(
          `https://api.microlink.io?url=${encodeURIComponent(url)}`
        );
        const data = await response.json();
        
        if (data.status === 'success') {
          setPreview({
            title: data.data.title,
            description: data.data.description,
            image: data.data.image?.url,
            siteName: data.data.publisher,
            loading: false,
            error: false,
          });
        } else {
          setPreview({ loading: false, error: true });
        }
      } catch (err) {
        setPreview({ loading: false, error: true });
      }
    };

    fetchPreview();
  }, [url]);

  // Se erro, mostra só o link destacado
  if (preview.error) {
    return (
      <div className="mt-2 p-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-[#2563EB] hover:underline flex items-center gap-2 break-all"
        >
          <Globe className="h-4 w-4 flex-shrink-0" />
          {url}
        </a>
      </div>
    );
  }

  if (preview.loading) {
  return (
    <div className="mt-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-tertiary)] overflow-hidden animate-pulse">
      {/* Skeleton da imagem - mesmo tamanho da imagem real */}
      <div className="w-full h-40 bg-[var(--border-color)]"></div>
      
      {/* Skeleton do conteúdo */}
      <div className="p-4 space-y-3">
        {/* Site name */}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[var(--border-color)]"></div>
          <div className="h-3 bg-[var(--border-color)] rounded w-24"></div>
        </div>
        
        {/* Título */}
        <div className="h-5 bg-[var(--border-color)] rounded w-3/4"></div>
        
        {/* Descrição - 3 linhas */}
        <div className="space-y-2">
          <div className="h-3 bg-[var(--border-color)] rounded w-full"></div>
          <div className="h-3 bg-[var(--border-color)] rounded w-full"></div>
          <div className="h-3 bg-[var(--border-color)] rounded w-2/3"></div>
        </div>
      </div>
      
      {/* Skeleton do link destacado embaixo */}
      <div className="mx-4 mb-4 p-2.5 rounded-lg bg-[var(--bg-secondary)]">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-[var(--border-color)]"></div>
          <div className="h-3 bg-[var(--border-color)] rounded flex-1"></div>
          <div className="w-3 h-3 bg-[var(--border-color)] rounded"></div>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="mt-3">
      {/* Preview Card */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-xl border border-[var(--border-color)] bg-[var(--bg-tertiary)] overflow-hidden hover:bg-[var(--bg-secondary)] transition-all duration-200 hover:shadow-md hover:border-[#2563EB]/30"
      >
        {/* Imagem do Preview */}
        {preview.image && (
          <div className="w-full h-40 overflow-hidden bg-[var(--bg-secondary)]">
            <img
              src={preview.image}
              alt={preview.title || 'Preview'}
              className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
        
        {/* Conteúdo do Preview */}
        <div className="p-4">
          {/* Nome do Site */}
          {preview.siteName && (
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 rounded bg-[var(--bg-secondary)] flex items-center justify-center">
                <Globe className="h-3 w-3 text-[var(--text-muted)]" />
              </div>
              <p className="text-xs text-[var(--text-muted)] uppercase tracking-wide font-medium">
                {preview.siteName}
              </p>
            </div>
          )}
          
          {/* Título */}
          {preview.title && (
            <p className="text-sm font-semibold text-[var(--text-primary)] line-clamp-2 leading-snug">
              {preview.title}
            </p>
          )}
          
          {/* Descrição */}
          {preview.description && (
            <p className="text-xs text-[var(--text-secondary)] mt-2 line-clamp-3 leading-relaxed">
              {preview.description}
            </p>
          )}
        </div>
      </a>
      
      {/* Link Destacado Embaixo */}
      <div className="mt-2 p-2.5 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-[#2563EB]/50 transition-colors">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#2563EB] hover:text-[#1d4ed8] flex items-center gap-2 group"
        >
          <div className="w-5 h-5 rounded-full bg-[#2563EB]/10 flex items-center justify-center group-hover:bg-[#2563EB]/20 transition-colors">
            <Globe className="h-3 w-3 text-[#2563EB]" />
          </div>
          <span className="truncate font-medium">{url}</span>
          <svg className="h-3 w-3 ml-auto flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  );
}

function TextWithLinks({ content }: { content: string }) {
  const urls = extractUrls(content);
  const firstUrl = urls.length > 0 ? urls[0] : null;
  
  // Remove a primeira URL do texto se tiver preview
  const cleanContent = firstUrl 
    ? content.replace(firstUrl, '').trim() 
    : content;

  const renderTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#2563EB] hover:underline break-all"
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div>
      {/* Texto sem a URL do preview */}
      {cleanContent && (
        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
          {renderTextWithLinks(cleanContent)}
        </p>
      )}
      
      {/* Preview com link embaixo */}
      {firstUrl && <LinkPreview url={firstUrl} />}
    </div>
  );
}
interface MessageBubbleProps {
  message: Message;
  index: number;
}

function MessageBubble({ message, index }: MessageBubbleProps) {
  const isAgent = message.sender === "agent";

  const getStatusIcon = () => {
    switch (message.status) {
      case "sending":
        return <Clock className="h-3 w-3 text-[var(--text-muted)]" />;
      case "sent":
        return <Check className="h-3 w-3 text-[var(--text-muted)]" />;
      case "delivered":
        return <CheckCheck className="h-3 w-3 text-[var(--text-muted)]" />;
      case "read":
        return <CheckCheck className="h-3 w-3 text-[#2563EB]" />;
      default:
        return null;
    }
  };

  if (message.isDeleted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.02 }}
        className={cn("flex", isAgent ? "justify-end" : "justify-start")}
      >
        <div className="max-w-[70%] rounded-2xl px-4 py-3 bg-[var(--bg-tertiary)] text-[var(--text-muted)] italic">
          <p className="text-sm line-through opacity-60">{message.content}</p>
          <p className="text-xs mt-1">🚫 {i18n.t("v2.chat.messageDeleted")}</p>
          <div className={cn("mt-2 flex items-center gap-1", isAgent ? "justify-end" : "justify-start")}>
            <span className="text-xs text-[var(--text-muted)]">{message.timestamp}</span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.02 }}
      className={cn("flex", isAgent ? "justify-end" : "justify-start")}
    >
      <div
        className={cn(
          "max-w-[70%] rounded-2xl px-4 py-3 shadow-sm transition-colors duration-300",
          isAgent
            ? "bg-[var(--message-outgoing)] text-[var(--text-primary)]"
            : "bg-[var(--message-incoming)] text-[var(--text-primary)]"
        )}
      >
        {/* Text Message */}
        {message.type === "text" && (
       <TextWithLinks content={message.content} />
        )}


        {/* Image Message */}
        {message.type === "image" && message.mediaUrl && (
          <div className="overflow-hidden rounded-xl mb-2">
            <img
              src={message.mediaUrl}
              alt="Imagem"
              className="max-w-full max-h-[300px] rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => window.open(message.mediaUrl, "_blank")}
            />
            {message.content && message.content !== message.fileName && (
              <p className="text-sm leading-relaxed whitespace-pre-wrap mt-2">
                {message.content}
              </p>
            )}
          </div>
        )}

        {/* Video Message */}
        {message.type === "video" && message.mediaUrl && (
          <div className="overflow-hidden rounded-xl mb-2">
            <video
              src={message.mediaUrl}
              controls
              className="max-w-full max-h-[300px] rounded-xl"
            />
            {message.content && message.content !== message.fileName && (
              <p className="text-sm leading-relaxed whitespace-pre-wrap mt-2">
                {message.content}
              </p>
            )}
          </div>
        )}

        {/* Audio Message */}
        {message.type === "audio" && message.mediaUrl && (
          <div className="flex items-center gap-3 min-w-[200px]">
            <audio controls className="w-full max-w-[250px]">
              <source src={message.mediaUrl} type="audio/ogg" />
              <source src={message.mediaUrl.replace(".ogg", ".mp3")} type="audio/mp3" />
            </audio>
          </div>
        )}

        {/* File Message */}
        {message.type === "file" && message.mediaUrl && (
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2563EB]/10">
              <FileText className="h-5 w-5 text-[#2563EB]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                {message.fileName || i18n.t("v2.chat.file")}
              </p>
              <a
                href={message.mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#2563EB] hover:underline flex items-center gap-1"
              >
                <Download className="h-3 w-3" />
                {i18n.t("v2.chat.download")}
              </a>
            </div>
          </div>
        )}

        {/* Sticker Message */}
        {message.type === "sticker" && message.mediaUrl && (
          <div className="overflow-hidden">
            <img
              src={message.mediaUrl}
              alt="Sticker"
              className="max-w-[120px] max-h-[120px]"
            />
          </div>
        )}

        {/* Location Message */}
        {message.type === "location" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-xl bg-[var(--bg-tertiary)] p-3">
              <MapPin className="h-5 w-5 text-[#EF4444]" />
              <span className="text-sm text-[var(--text-primary)]">
                {message.content || i18n.t("v2.messageInput.attachments.location")}
              </span>
            </div>
          </div>
        )}

        {/* Timestamp and Status */}
        <div className={cn("mt-2 flex items-center gap-1", isAgent ? "justify-end" : "justify-start")}>
          <span className="text-xs text-[var(--text-muted)]">{message.timestamp}</span>
          {isAgent && getStatusIcon()}
        </div>
      </div>
    </motion.div>
  );
}
